// src/components/ContactForm.jsx
import { useState } from "react";
import { FaUser, FaEnvelope, FaPhone, FaPaperPlane } from "react-icons/fa";
import { motion } from "framer-motion";
import { ConfirmationPopup } from "./ConfirmationPopup";

const serviceTypes = [
  { value: "freelance", label: "Freelance Project" },
  { value: "corporate", label: "Corporate Partnership" },
  { value: "project", label: "Project Collaboration" },
  { value: "collaboration", label: "Startup Collaboration" },
  { value: "consultancy", label: "Technical Consultancy" },
  { value: "other", label: "Other" },
];

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    serviceType: "freelance",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const apiUrl = "http://localhost:3001/api/contact";

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          serviceType: "freelance",
        });
        setShowPopup(true);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setError("Network error. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
  };

  return (
    <>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-[#e5e5e0] border border-black/10 rounded-2xl shadow-xl p-8 md:p-10"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-amiamie font-bold mb-2 text-black">
              Let's Build Together
            </h2>
            <p className="text-black/60">
              Ready to bring your ideas to life? Let's connect and make
              something amazing.
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg"
            >
              <p className="text-red-600 text-sm">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium mb-2 text-black"
                >
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="w-full pl-10 pr-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder:text-black/40 transition-all duration-200"
                    placeholder="John Doe"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2 text-black"
                >
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full pl-10 pr-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder:text-black/40 transition-all duration-200"
                    placeholder="john@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium mb-2 text-black"
              >
                Phone Number
              </label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  className="w-full pl-10 pr-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder:text-black/40 transition-all duration-200"
                  placeholder="+1 (555) 000-0000"
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="serviceType"
                className="block text-sm font-medium mb-2 text-black"
              >
                I'm interested in <span className="text-red-500">*</span>
              </label>
              <select
                id="serviceType"
                value={formData.serviceType}
                onChange={(e) =>
                  setFormData({ ...formData, serviceType: e.target.value })
                }
                className="w-full px-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-black appearance-none transition-all duration-200"
                required
                disabled={isLoading}
              >
                {serviceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="subject"
                className="block text-sm font-medium mb-2 text-black"
              >
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                className="w-full px-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder:text-black/40 transition-all duration-200"
                placeholder="Brief subject line"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label
                htmlFor="message"
                className="block text-sm font-medium mb-2 text-black"
              >
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                rows={5}
                className="w-full px-4 py-3 border border-black/20 rounded-lg bg-white/50 focus:outline-none focus:ring-2 focus:ring-black/30 text-black placeholder:text-black/40 resize-none transition-all duration-200"
                placeholder="Tell me about your project, ideas, or how I can help..."
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-8 py-4 bg-black text-white rounded-lg hover:bg-black/80 transition-all duration-300 font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane className="text-lg" />
                  Send Message
                </>
              )}
            </button>

            <p className="text-xs text-black/40 text-center mt-4">
              I'll respond within 24 hours. Your information will never be
              shared.
            </p>
          </form>
        </motion.div>
      </div>

      {/* Confirmation Popup */}
      <ConfirmationPopup isOpen={showPopup} onClose={closePopup} />
    </>
  );
}
